import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useQueries } from "@tanstack/react-query";
import { map } from "lodash-es";
import { useMemo, useState } from "react";

import EmptySearchImage from "@/assets/images/empty-search.svg?react";
import { VirtualizedItemList } from "@/components/item-list";
import {
  EmptyContainer,
  EmptyText,
  EmptyTextContainer,
  EmptyTextSubtitle,
} from "@/components/styling/empty";
import { ItemListRowType } from "@/components/item-list/types";
import type { ItemListRowData } from "@/components/item-list/types";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSLoadingSpinner,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { getAgentActivity } from "@/pages/agent/activities/agent-activity-api";
import {
  AgentActivityText,
  resolveAgentActivityText,
} from "@/pages/agent/activities/agent-activity-text";
import type { AgentActivityNavigation } from "@/pages/agent/activities/agent-activity-text";
import type {
  AgentActivityItem,
  AgentActivitySummary,
} from "@/pages/agent/activities/agent-activity.types";
import {
  formatAgentActivityDateTime,
  formatAgentActivityDay,
  formatAgentActivityItemTime,
  getAgentActivityDay,
  getAgentActivityIcon,
} from "@/pages/agent/activities/agent-activity.utils";

const PARENT_ROW_MIN_HEIGHT = 72;

export const AgentActivityList = ({
  activities,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  onLoadMore,
  onRetry,
  loadActivityDetail = getAgentActivity,
  initialExpandedActivityIds = [],
  hasActiveFilters = false,
  ...navigation
}: {
  activities: AgentActivitySummary[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  loadActivityDetail?: typeof getAgentActivity;
  initialExpandedActivityIds?: string[];
  hasActiveFilters?: boolean;
} & AgentActivityNavigation) => {
  const [expandedActivityIds, setExpandedActivityIds] = useState<Set<string>>(
    () => new Set(initialExpandedActivityIds)
  );
  const [scrollTop, setScrollTop] = useState(0);
  const detailQueries = useQueries({
    queries: map(activities, activity => ({
      queryKey: ["agent-activity", activity.id],
      queryFn: () => loadActivityDetail({ activityId: activity.id }),
      enabled: expandedActivityIds.has(activity.id) && activity.item_count > 0,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const rows = useMemo<ItemListRowData[]>(() => {
    const nextRows: ItemListRowData[] = [];
    let currentDay: string | null = null;

    activities.forEach((activity, index) => {
      const day = getAgentActivityDay({ occurredAt: activity.occurred_at });
      if (day !== currentDay) {
        currentDay = day;
        nextRows.push({
          key: `day-${day}`,
          type: ItemListRowType.Other,
          payload: {
            minHeightPx: 0,
            children: (
              <StyledDayRow $showRail={nextRows.length > 0}>
                <StyledDayHeader>
                  {formatAgentActivityDay({ occurredAt: activity.occurred_at })}
                </StyledDayHeader>
              </StyledDayRow>
            ),
          },
        });
      }

      const isExpanded = expandedActivityIds.has(activity.id);
      const detailQuery = detailQueries[index];
      nextRows.push({
        key: `activity-${activity.id}`,
        type: ItemListRowType.Other,
        payload: {
          children: (
            <AgentActivityParentRow
              activity={activity}
              isExpanded={isExpanded}
              onToggle={() => {
                setExpandedActivityIds(currentIds => {
                  const nextIds = new Set(currentIds);
                  if (nextIds.has(activity.id)) {
                    nextIds.delete(activity.id);
                  } else {
                    nextIds.add(activity.id);
                  }
                  return nextIds;
                });
              }}
              {...navigation}
            />
          ),
        },
      });

      if (!isExpanded || activity.item_count === 0) return;

      if (detailQuery.isLoading) {
        nextRows.push({
          key: `detail-loading-${activity.id}`,
          type: ItemListRowType.Other,
          payload: {
            children: (
              <StyledChildRowShell aria-label="Loading activity details">
                <StyledChildRow $isLast>
                  <StyledChildTime>
                    <MDSSkeleton width={52} height={12} />
                  </StyledChildTime>
                  <StyledChildCopy>
                    <MDSSkeleton width={260} height={14} />
                  </StyledChildCopy>
                </StyledChildRow>
              </StyledChildRowShell>
            ),
          },
        });
        return;
      }

      if (detailQuery.isError) {
        nextRows.push({
          key: `detail-error-${activity.id}`,
          type: ItemListRowType.Other,
          payload: {
            children: (
              <StyledChildRowShell>
                <StyledDetailState>
                  <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                    Could not load activity details.
                  </MDSText>
                  <MDSButton
                    size={MDSButtonSize.Small}
                    variant={MDSButtonVariant.Transparent}
                    onPress={() => {
                      void detailQuery.refetch();
                    }}
                  >
                    Try again
                  </MDSButton>
                </StyledDetailState>
              </StyledChildRowShell>
            ),
          },
        });
        return;
      }

      (detailQuery.data?.items ?? []).forEach((item, itemIndex, items) => {
        nextRows.push({
          key: `activity-item-${item.id}`,
          type: ItemListRowType.Other,
          payload: {
            children: (
              <AgentActivityItemRow
                item={item}
                parentOccurredAt={activity.occurred_at}
                isLast={itemIndex === items.length - 1}
                {...navigation}
              />
            ),
          },
        });
      });
    });

    if (isFetchingNextPage) {
      nextRows.push({
        key: "next-page-loading",
        type: ItemListRowType.Other,
        payload: {
          children: (
            <StyledNextPageState aria-label="Loading earlier activity">
              <MDSLoadingSpinner size={18} />
            </StyledNextPageState>
          ),
        },
      });
    } else if (isFetchNextPageError) {
      nextRows.push({
        key: "next-page-error",
        type: ItemListRowType.Other,
        payload: {
          children: (
            <StyledNextPageState>
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                Could not load earlier activity.
              </MDSText>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onPress={onLoadMore}
              >
                Try again
              </MDSButton>
            </StyledNextPageState>
          ),
        },
      });
    }

    return nextRows;
  }, [
    activities,
    detailQueries,
    expandedActivityIds,
    isFetchNextPageError,
    isFetchingNextPage,
    navigation,
    onLoadMore,
  ]);

  if (isLoading) {
    return <AgentActivityListSkeleton />;
  }

  if (isError && activities.length === 0) {
    return (
      <StyledInitialState>
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Could not load the activity log.
        </MDSText>
        <MDSButton size={MDSButtonSize.Small} variant={MDSButtonVariant.Chrome} onPress={onRetry}>
          Try again
        </MDSButton>
      </StyledInitialState>
    );
  }

  if (activities.length === 0) {
    if (hasActiveFilters) {
      return (
        <EmptyContainer>
          <EmptySearchImage />
          <EmptyText>No activity matches these filters</EmptyText>
        </EmptyContainer>
      );
    }
    return (
      <EmptyContainer>
        <EmptySearchImage />
        <EmptyTextContainer>
          <EmptyText>No activity yet</EmptyText>
          <EmptyTextSubtitle>Actions your agent takes will appear here</EmptyTextSubtitle>
        </EmptyTextContainer>
      </EmptyContainer>
    );
  }

  return (
    <StyledListViewport aria-label="Activity log">
      <StyledListFill>
        <VirtualizedItemList
          endReached={() => {
            if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
              onLoadMore();
            }
          }}
          registerWithNavigation={false}
          rows={rows}
          scrollTop={scrollTop}
          setScrollTop={setScrollTop}
        />
      </StyledListFill>
    </StyledListViewport>
  );
};

const AgentActivityParentRow = ({
  activity,
  isExpanded,
  onToggle,
  ...navigation
}: {
  activity: AgentActivitySummary;
  isExpanded: boolean;
  onToggle: () => void;
} & AgentActivityNavigation) => {
  const canExpand = activity.item_count > 0;
  const resolvedText = resolveAgentActivityText({
    text: activity.display.primary_text,
    references: activity.references ?? [],
  });

  return (
    <StyledTimelineRow>
      <StyledTimelineDot aria-hidden="true" />
      <StyledParentRow
        $canExpand={canExpand}
        $isExpanded={isExpanded}
        onClick={canExpand ? onToggle : undefined}
      >
        <StyledParentTime>
          {formatAgentActivityDateTime({ occurredAt: activity.occurred_at })}
        </StyledParentTime>
        <StyledActivityCopy>
          <AgentActivityText
            resolved={resolvedText}
            weight={MDSTextWeight.SemiBold}
            {...navigation}
          />
          {activity.display.secondary_text ? (
            <StyledSecondary>
              <MDSIcon
                kind={getAgentActivityIcon({
                  icon: activity.display.icon,
                  sourceKind: activity.source.kind,
                })}
                size={14}
              />
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                {activity.display.secondary_text}
              </MDSText>
            </StyledSecondary>
          ) : null}
        </StyledActivityCopy>
        {canExpand ? (
          <StyledParentExpandButton
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse activity details" : "Expand activity details"}
            type="button"
            onClick={event => {
              event.stopPropagation();
              onToggle();
            }}
          >
            <MDSIcon kind={isExpanded ? MDSIconKind.AngleUp : MDSIconKind.AngleDown} size={14} />
          </StyledParentExpandButton>
        ) : null}
      </StyledParentRow>
    </StyledTimelineRow>
  );
};

const AgentActivityItemRow = ({
  item,
  parentOccurredAt,
  isLast,
  ...navigation
}: {
  item: AgentActivityItem;
  parentOccurredAt: string;
  isLast: boolean;
} & AgentActivityNavigation) => {
  const resolvedText = resolveAgentActivityText({
    text: item.display.primary_text,
    references: item.references ?? [],
  });
  return (
    <StyledChildRowShell>
      <StyledChildRow $isLast={isLast}>
        <StyledChildTime>
          {formatAgentActivityItemTime({
            occurredAt: item.occurred_at,
            parentOccurredAt,
          })}
        </StyledChildTime>
        <StyledChildCopy>
          <StyledChildPrimary>
            {item.display.icon && !resolvedText.hasLinkableReference ? (
              <MDSIcon kind={getAgentActivityIcon({ icon: item.display.icon })} size={14} />
            ) : null}
            <AgentActivityText resolved={resolvedText} {...navigation} />
          </StyledChildPrimary>
          {item.display.secondary_text ? (
            <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
              {item.display.secondary_text}
            </MDSText>
          ) : null}
        </StyledChildCopy>
      </StyledChildRow>
    </StyledChildRowShell>
  );
};

const SKELETON_CARDS = [
  { time: 96, title: 300, meta: 128 },
  { time: 88, title: 240, meta: 150 },
  { time: 96, title: 340, meta: 110 },
  { time: 92, title: 260, meta: 140 },
];

const AgentActivityListSkeleton = () => (
  <StyledSkeletonViewport aria-label="Loading activity log">
    <StyledDayRow $showRail={false}>
      <StyledDayHeader as="div">
        <MDSSkeleton width={56} height={13} />
      </StyledDayHeader>
    </StyledDayRow>
    {SKELETON_CARDS.map((card, index) => (
      <StyledTimelineRow key={index}>
        <StyledTimelineDot aria-hidden="true" />
        <StyledSkeletonCard>
          <StyledSkeletonTime>
            <MDSSkeleton width={card.time} height={14} />
          </StyledSkeletonTime>
          <StyledSkeletonCopy>
            <MDSSkeleton width={card.title} height={15} />
            <MDSSkeleton width={card.meta} height={13} />
          </StyledSkeletonCopy>
        </StyledSkeletonCard>
      </StyledTimelineRow>
    ))}
  </StyledSkeletonViewport>
);

const StyledSkeletonViewport = styled.div`
  container-type: inline-size;
  flex: 1 1 auto;
  min-height: 320px;
  overflow: hidden;
`;

const StyledSkeletonCard = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: 28px;
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: 150px minmax(0, 1fr) 18px;
  min-height: ${PARENT_ROW_MIN_HEIGHT}px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};

  @container (max-width: 700px) {
    grid-template-columns: 1fr 18px;
  }
`;

const StyledSkeletonTime = styled.div`
  display: flex;
  justify-content: flex-end;

  @container (max-width: 700px) {
    display: none;
  }
`;

const StyledSkeletonCopy = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
`;

const StyledListViewport = styled.div`
  container-type: inline-size;
  flex: 1 1 auto;
  min-height: 320px;
  position: relative;
`;

const StyledListFill = styled.div`
  inset: 0;
  position: absolute;
`;

const StyledInitialState = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
  min-height: 240px;
`;

const timelineRail = css`
  &::before {
    border-left: 1px dashed var(--agent-activity-rail-color);
    bottom: 0;
    content: "";
    left: 7px;
    position: absolute;
    top: 0;
  }
`;

const StyledDayRow = styled.div<{ $showRail: boolean }>`
  --agent-activity-rail-color: ${({ theme }) => theme.colors.border.default};
  position: relative;

  ${({ $showRail }) => ($showRail ? timelineRail : "")}
`;

const StyledDayHeader = styled.h2`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextS]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  margin: ${({ theme }) => `${theme.spacing[5]} 0 ${theme.spacing[3]} ${theme.spacing[8]}`};
  padding-left: ${({ theme }) => theme.spacing[6]};
  text-transform: uppercase;
`;

const StyledTimelineRow = styled.div`
  --agent-activity-rail-color: ${({ theme }) => theme.colors.border.default};
  padding: ${({ theme }) => `${theme.spacing[4]} 0 0 ${theme.spacing[8]}`};
  position: relative;

  ${timelineRail}
`;

const StyledTimelineDot = styled.span`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 2px solid ${({ theme }) => theme.colors.surface.chrome};
  border-radius: 999px;
  height: 14px;
  left: 1px;
  position: absolute;
  top: ${({ theme }) => `calc(${theme.spacing[4]} + ${PARENT_ROW_MIN_HEIGHT / 2}px)`};
  transform: translateY(-50%);
  width: 14px;
  z-index: 1;
`;

const StyledParentRow = styled.div<{ $canExpand: boolean; $isExpanded: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 0;
  border-radius: 28px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: ${({ $canExpand }) => ($canExpand ? "pointer" : "default")};
  display: grid;
  font: inherit;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: 150px minmax(0, 1fr) 18px;
  min-height: ${PARENT_ROW_MIN_HEIGHT}px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  text-align: left;
  width: 100%;

  border-radius: ${({ $isExpanded }) => ($isExpanded ? "28px 28px 0 0" : "28px")};

  ${({ $canExpand, theme }) =>
    $canExpand &&
    `&:hover {
      background-image: linear-gradient(
        ${theme.colors.background.hoverOverlay["chrome-2"]},
        ${theme.colors.background.hoverOverlay["chrome-2"]}
      );
    }`}

  @container (max-width: 700px) {
    gap: ${({ theme }) => theme.spacing[2]};
    grid-template-columns: 1fr 18px;
  }
`;

const StyledParentExpandButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 999px;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 28px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 1px;
  }
`;

const StyledParentTime = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextS]};
  text-align: right;

  @container (max-width: 700px) {
    display: none;
  }
`;

const StyledActivityCopy = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledSecondary = styled.span`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledChildRowShell = styled.div`
  --agent-activity-rail-color: ${({ theme }) => theme.colors.border.default};
  padding-left: ${({ theme }) => theme.spacing[8]};
  position: relative;

  ${timelineRail}
`;

const StyledChildRow = styled.div<{ $isLast: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-bottom: ${({ $isLast, theme }) =>
    $isLast ? "none" : `1px solid ${theme.colors.border.chrome}`};
  border-radius: ${({ $isLast }) => ($isLast ? "0 0 28px 28px" : "0")};
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: 150px minmax(0, 1fr);
  min-height: 64px;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};

  @container (max-width: 700px) {
    grid-template-columns: 88px minmax(0, 1fr);
  }
`;

const StyledChildTime = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextS]};
  text-align: right;
`;

const StyledChildCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledChildPrimary = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledDetailState = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: 0 0 28px 28px;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: center;
  min-height: 64px;
`;

const StyledNextPageState = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: center;
  min-height: 64px;
`;
